import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { InfoCard } from '@backstage/core-components';
import { Typography, makeStyles } from '@material-ui/core';
import { YamlEditorPage } from '../YamlEditorPage';

/** Check if URL points to GitHub using proper hostname validation */
const isGitHubHost = (urlString: string): boolean => {
  try {
    const url = new URL(urlString.replace(/^url:/, ''));
    const host = url.hostname.toLowerCase();
    return host === 'github.com' || host.endsWith('.github.com');
  } catch {
    return false;
  }
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    height: 'calc(100vh - 250px)', // Responsive to viewport minus header/tabs
    minHeight: 500,
  },
  editorWrapper: {
    height: '100%',
  },
}));

export interface EntityYamlEditorContentProps {
  /**
   * Optional custom file path to edit instead of catalog-info.yaml
   */
  filePath?: string;
}

/**
 * Entity page content component for the YAML Editor.
 * Automatically extracts entity information from the current entity context.
 */
export const EntityYamlEditorContent = ({
  filePath,
}: EntityYamlEditorContentProps) => {
  const classes = useStyles();
  const { entity } = useEntity();

  // Check if entity has a source location
  const sourceLocation =
    entity.metadata.annotations?.['backstage.io/source-location'] ||
    entity.metadata.annotations?.['backstage.io/managed-by-location'];

  if (!sourceLocation) {
    return (
      <InfoCard title="YAML Editor">
        <Typography variant="body1" color="textSecondary">
          This entity does not have a source location annotation, so the YAML
          editor cannot determine where to fetch the catalog-info.yaml file
          from.
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 16 }}
        >
          To enable the YAML editor, ensure your entity has one of the following
          annotations:
        </Typography>
        <ul>
          <li>
            <code>backstage.io/source-location</code>
          </li>
          <li>
            <code>backstage.io/managed-by-location</code>
          </li>
        </ul>
      </InfoCard>
    );
  }

  const isGitHub = isGitHubHost(sourceLocation);

  if (!isGitHub) {
    return (
      <InfoCard title="YAML Editor">
        <Typography variant="body1" color="textSecondary">
          The YAML editor currently only supports GitHub repositories.
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 8 }}
        >
          Source location: {sourceLocation}
        </Typography>
      </InfoCard>
    );
  }

  const entityRef = stringifyEntityRef(entity);
  const entityName = entity.metadata.name;

  return (
    <div className={classes.container}>
      <div className={classes.editorWrapper}>
        <YamlEditorPage
          entityRef={entityRef}
          entityName={entityName}
          filePath={filePath}
        />
      </div>
    </div>
  );
};

